import app from 'flarum/forum/app';
import { resolveTemplate } from './recitalTokens';

/**
 * Option B (materialize-early): when a recital is created, its announcement and
 * submission discussions are created immediately as HIDDEN (coordinator-only),
 * pre-filled from the per-type templates, and linked to the recital. The
 * coordinator then edits them natively; "Create Announcement" / "Open
 * Submissions" later reveal them.
 *
 * Discussions are tagged with the Recital Hall tag but hidden, so members never
 * see them in the hall or search until they are revealed.
 */

function findType(typeId) {
  var types = app.forum.attribute('recitalsRecitalTypes') || [];
  for (var i = 0; i < types.length; i++) {
    if (String(types[i].id) === String(typeId)) return types[i];
  }
  return null;
}
function recitalHallTag() {
  var tagId = app.forum.attribute('recitalsRecitalTagId') || 20;
  var tags = app.store && app.store.all ? app.store.all('tags') : [];
  for (var i = 0; i < tags.length; i++) {
    if (tags[i] && String(tags[i].id()) === String(tagId)) return tags[i];
  }
  return null;
}
function createHiddenDiscussion(title, content, hallTag) {
  return app.store.createRecord('discussions').save({
    title: title,
    content: content,
    relationships: {
      tags: hallTag ? [hallTag] : []
    }
  }).then(function (discussion) {
    return discussion.save({
      isHidden: true
    }).then(function () {
      return discussion;
    });
  });
}

// Normalized /d/{id}-{slug} link (slug omitted if unavailable), matching the
// PHP TokenResolver shape.
function discussionLink(discussion, id) {
  var slug = discussion && discussion.slug ? discussion.slug() : null;
  return '/d/' + id + (slug ? '-' + slug : '');
}

// Surgically fill the cross-reference link tokens in a discussion's first post
// once both threads exist. The announcement and submission threads link to each
// other ({internal_recital_link} <-> {internal_meta_link}), so whichever is
// created first can't resolve the link to the other at creation; this second
// pass fills it in. No-op (and no save) when neither token is present, so it is
// idempotent and never clobbers unrelated native edits.
function fillCrossLinks(discussion, metaLink, recitalLink) {
  if (!discussion) return Promise.resolve();
  var first = discussion.firstPost && discussion.firstPost();
  if (!first) return Promise.resolve();
  var src = first.content && first.content();
  if (src == null) return Promise.resolve();
  if (src.indexOf('{internal_meta_link}') === -1 && src.indexOf('{internal_recital_link}') === -1) {
    return Promise.resolve();
  }
  var updated = src.split('{internal_meta_link}').join(metaLink).split('{internal_recital_link}').join(recitalLink);
  if (updated === src) return Promise.resolve();
  return first.save({
    content: updated
  });
}

/**
 * Ensure the recital's announcement + submission discussions exist (hidden),
 * creating whichever is missing. Idempotent — safe to re-run to fill gaps after
 * a partial failure. Resolves to the (updated) recital.
 */
export function materializeRecital(recital) {
  var type = findType(recital.recitalTypeId());
  var label = type && type.label ? type.label : 'Recital';
  var number = recital.recitalNumber();
  var hallTag = recitalHallTag();
  var pastRecitals = (app.forum.attribute('recitalsPastRecitals') || {})[recital.recitalTypeId()] || '';
  var ctx = {
    label: label,
    number: number,
    placeholderValues: recital.placeholderValues && recital.placeholderValues() || {},
    pastRecitals: pastRecitals
  };
  var chain = Promise.resolve(recital);
  if (!recital.announcementDiscussionId()) {
    chain = chain.then(function () {
      var title = resolveTemplate(type && type.announcement_creation_title, ctx) || label + ' #' + number + ' — Announcement';
      var content = resolveTemplate(type && type.announcement_template, ctx) || '#### ' + label + ' #' + number + ' is coming!\n\n*(Draft — edit this announcement.)*';
      return createHiddenDiscussion(title, content, hallTag).then(function (d) {
        return recital.save({
          announcementDiscussionId: Number(d.id())
        });
      });
    });
  }
  if (!recital.submissionDiscussionId()) {
    chain = chain.then(function () {
      var title = resolveTemplate(type && type.submission_creation_title, ctx) || label + ' #' + number + ' — Submissions';
      // Leave any {internal_*_link} tokens for the reconcile pass below, which
      // fills them once both the announcement and submission threads exist.
      var content = resolveTemplate(type && type.submission_opening_body, ctx) || '#### ' + label + ' #' + number + ' — Submissions\n\n*(Draft — edit the submission instructions.)*';
      return createHiddenDiscussion(title, content, hallTag).then(function (d) {
        return recital.save({
          submissionDiscussionId: Number(d.id())
        });
      });
    });
  }

  // Reconcile cross-reference links once BOTH threads exist: the announcement
  // post may reference {internal_recital_link} (the submission thread) and the
  // submission post may reference {internal_meta_link} (the announcement thread).
  // Reload both so the first-post source is available even on an idempotent
  // re-run that didn't freshly create one of them.
  chain = chain.then(function () {
    var annId = recital.announcementDiscussionId && recital.announcementDiscussionId();
    var subId = recital.submissionDiscussionId && recital.submissionDiscussionId();
    if (!annId || !subId) return recital;
    return Promise.all([app.store.find('discussions', String(annId)), app.store.find('discussions', String(subId))]).then(function (discs) {
      var ann = discs[0];
      var sub = discs[1];
      var metaLink = discussionLink(ann, annId);
      var recitalLink = discussionLink(sub, subId);
      return Promise.all([fillCrossLinks(ann, metaLink, recitalLink), fillCrossLinks(sub, metaLink, recitalLink)]);
    }).then(function () {
      return recital;
    });
  });
  return chain;
}